import { canPreviewWorkflowRuleGroups } from "../workflow-rule-groups.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { ControlledPerformanceReviewInvitationFields } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewInvitationFields.tsx";
import { usePerformanceReviewTemplateSearch } from "@/queries/performance-reviews/use-performance-review-template.query.ts";
import { ApiFormTemplateFilter } from "@/types/forms/form-template.types.ts";
import {
  ApiPerformanceReviewInvitationMode,
  ApiPerformanceReviewReviewerTypes,
} from "@/types/performance-review.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { ApiWorkflowEventTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { Box, Input, Select, Stack, Switch, Title, useModals } from "@skailer-com/uikit";
import { useEffect, useMemo, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ANONYMOUS_THRESHOLD_MIN,
  getAddableReviewerTypeOptions,
  getReviewerTypeTitle,
  isAnonymousSupportedReviewerType,
  REVIEWER_TYPES_ENABLED_VALIDATION_FIELD,
} from "./helpers";
import { WorkflowPerformanceReviewFormData, WorkflowSystemReviewerType } from "./types";
import { PerformanceReviewReviewerTypeAddOrEditModal } from "@/pages/talent/performance-reviews/edit/widgets/PerformanceReviewReviewerTypeAddOrEditModal.tsx";

const REVIEWER_TYPE_SORT_ORDER: Record<ApiPerformanceReviewReviewerTypes, number> = {
  [ApiPerformanceReviewReviewerTypes.myself]: 0,
  [ApiPerformanceReviewReviewerTypes.manager]: 1,
  [ApiPerformanceReviewReviewerTypes.upward]: 2,
  [ApiPerformanceReviewReviewerTypes.peer]: 3,
  [ApiPerformanceReviewReviewerTypes.custom]: 4,
};

interface WorkflowPerformanceReviewReviewerTypesSectionProps {
  control: Control<WorkflowPerformanceReviewFormData>;
  register: UseFormRegister<WorkflowPerformanceReviewFormData>;
  trigger: UseFormTrigger<WorkflowPerformanceReviewFormData>;
  setValue: UseFormSetValue<WorkflowPerformanceReviewFormData>;
  errors: FieldErrors<WorkflowPerformanceReviewFormData>;
  eventType?: ApiWorkflowEventTypes | null;
}

export const WorkflowPerformanceReviewReviewerTypesSection = ({
  control,
  register,
  trigger,
  setValue,
  errors,
  eventType,
}: WorkflowPerformanceReviewReviewerTypesSectionProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const participantConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowPerformanceReviewReviewers
  );

  const [templateFilter, setTemplateFilter] = useState<ApiFormTemplateFilter>({
    page_number: 1,
    text: "",
  });

  const { fields, append } = useFieldArray({
    control,
    name: "block_data.reviewer_types",
  });

  const watchedReviewerTypes = useWatch({
    control,
    name: "block_data.reviewer_types",
  });
  const reviewerTypes = useMemo(() => watchedReviewerTypes ?? [], [watchedReviewerTypes]);
  const invitationMode = useWatch({ control, name: "block_data.invitation_mode" });

  const reviewerTypeIndexesForRender = useMemo(() => {
    return fields
      .map((_, index) => index)
      .filter((index) => reviewerTypes[index] != null)
      .sort((indexA, indexB) => {
        const reviewerTypeA = reviewerTypes[indexA]!.reviewer_type;
        const reviewerTypeB = reviewerTypes[indexB]!.reviewer_type;
        const orderA = REVIEWER_TYPE_SORT_ORDER[reviewerTypeA];
        const orderB = REVIEWER_TYPE_SORT_ORDER[reviewerTypeB];

        if (orderA !== orderB) return orderA - orderB;

        return indexA - indexB;
      });
  }, [fields, reviewerTypes]);

  const addableReviewerTypeOptions = useMemo(
    () => getAddableReviewerTypeOptions(t, reviewerTypes),
    [reviewerTypes, t]
  );

  const { data: performanceReviewTemplates, isLoading: isTemplatesLoading } =
    usePerformanceReviewTemplateSearch(templateFilter, true);

  const templateOptions = useMemo(() => {
    return (
      performanceReviewTemplates?.items.map((template) => ({
        id: template.id,
        title: template.title,
      })) ?? []
    );
  }, [performanceReviewTemplates]);

  const reviewerTypesErrorMessage = errors.block_data?.reviewer_types_enabled_validation?.message;

  useEffect(() => {
    if (!templateOptions.length) return;

    const selfReviewerIndex = reviewerTypes.findIndex(
      (item) => item.reviewer_type === ApiPerformanceReviewReviewerTypes.myself
    );

    if (selfReviewerIndex === -1) return;

    const currentSelfReviewer = reviewerTypes[selfReviewerIndex];
    if (currentSelfReviewer.form_template_id != null) return;

    const firstTemplateId = Number(templateOptions[0].id);
    if (Number.isNaN(firstTemplateId)) return;

    setValue(`block_data.reviewer_types.${selfReviewerIndex}.form_template_id`, firstTemplateId, {
      shouldValidate: true,
    });
  }, [reviewerTypes, setValue, templateOptions]);

  const handleAddReviewerType = (reviewerTypeToAdd: WorkflowSystemReviewerType) => {
    const existingReviewerTypeIndex = reviewerTypes.findIndex(
      (reviewerType) => reviewerType.reviewer_type === reviewerTypeToAdd
    );
    if (existingReviewerTypeIndex !== -1) {
      setValue(`block_data.reviewer_types.${existingReviewerTypeIndex}.is_enabled`, true, {
        shouldValidate: true,
      });
      void trigger(["block_data.reviewer_types", REVIEWER_TYPES_ENABLED_VALIDATION_FIELD]);
      return;
    }

    const supportsAnonymous = isAnonymousSupportedReviewerType(reviewerTypeToAdd);

    append({
      id: null,
      reviewer_type: reviewerTypeToAdd,
      title: null,
      form_template_id: null,
      is_enabled: true,
      is_anonymous: supportsAnonymous,
      anonymous_threshold: supportsAnonymous ? ANONYMOUS_THRESHOLD_MIN : 0,
      participants_rule_groups: [],
      invitation_subject: null,
      invitation_body: null,
    });

    void trigger(["block_data.reviewer_types", REVIEWER_TYPES_ENABLED_VALIDATION_FIELD]);
  };

  const handleAddCustomReviewerType = (title: string) => {
    const cleanTitle = title.trim();
    if (!cleanTitle) return;

    const normalizedTitle = cleanTitle.toLowerCase();
    const customTypeAlreadyExists = reviewerTypes.some(
      (item) =>
        item.reviewer_type === ApiPerformanceReviewReviewerTypes.custom &&
        (item.title ?? "").trim().toLowerCase() === normalizedTitle
    );
    if (customTypeAlreadyExists) return;

    append({
      id: null,
      reviewer_type: ApiPerformanceReviewReviewerTypes.custom,
      title: cleanTitle,
      form_template_id: null,
      is_enabled: true,
      is_anonymous: true,
      anonymous_threshold: ANONYMOUS_THRESHOLD_MIN,
      participants_rule_groups: [],
      invitation_subject: null,
      invitation_body: null,
    });

    void trigger(["block_data.reviewer_types", REVIEWER_TYPES_ENABLED_VALIDATION_FIELD]);
  };

  const handleReviewerTypeToggle = (index: number, isEnabled: boolean) => {
    setValue(`block_data.reviewer_types.${index}.is_enabled`, isEnabled, {
      shouldValidate: true,
    });
    void trigger(REVIEWER_TYPES_ENABLED_VALIDATION_FIELD);
  };

  const handleReviewerTypeAnonymousToggle = (index: number, isAnonymous: boolean) => {
    setValue(`block_data.reviewer_types.${index}.is_anonymous`, isAnonymous, {
      shouldValidate: true,
    });
    setValue(
      `block_data.reviewer_types.${index}.anonymous_threshold`,
      isAnonymous ? ANONYMOUS_THRESHOLD_MIN : 0,
      {
        shouldValidate: true,
      }
    );
  };

  return (
    <Stack>
      <Title
        header={t("performance_review:reviewers")}
        caption={t("performance_review:reviewers_description")}
        paddingBottom
        paddingTop
      />
      <Stack gap={"sm"}>
        {reviewerTypeIndexesForRender.map((index) => {
          const field = fields[index];
          if (!field) return null;
          const reviewerType = reviewerTypes[index];
          if (!reviewerType) return null;
          const canConfigureParticipants =
            reviewerType.reviewer_type === ApiPerformanceReviewReviewerTypes.peer ||
            reviewerType.reviewer_type === ApiPerformanceReviewReviewerTypes.custom;
          const canConfigureAnonymous = isAnonymousSupportedReviewerType(
            reviewerType.reviewer_type
          );
          const showAnonymitySettings = canConfigureAnonymous && reviewerType.is_anonymous;

          return (
            <Stack key={field.id} className={"rounded-xs border border-dark/10 bg-dark/5 p-4"}>
              <Controller
                control={control}
                name={`block_data.reviewer_types.${index}.is_enabled`}
                render={({ field: { value } }) => (
                  <Switch
                    className={"self-start"}
                    checked={value}
                    onChange={(nextValue) => handleReviewerTypeToggle(index, nextValue)}
                    title={getReviewerTypeTitle(t, reviewerType.reviewer_type, reviewerType.title)}
                  />
                )}
              />

              {reviewerType.is_enabled && (
                <>
                  <Stack gap={"md"}>
                    <Box className={"grow"}>
                      <Controller
                        control={control}
                        name={`block_data.reviewer_types.${index}.form_template_id`}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            label={t("common:template")}
                            emptyTitle={t("common:select")}
                            value={value ?? undefined}
                            searchable
                            isLoading={isTemplatesLoading}
                            options={templateOptions}
                            loadOptionsOnOpen
                            searchOptions={templateOptions}
                            onSearchOptions={(text) => {
                              setTemplateFilter((prev) => ({ ...prev, text }));
                            }}
                            onChange={(nextValue) => {
                              onChange(nextValue == null ? null : Number(nextValue));
                            }}
                            error={
                              errors.block_data?.reviewer_types?.[index]?.form_template_id?.message
                            }
                          />
                        )}
                      />
                    </Box>

                    {canConfigureAnonymous && (
                      <Controller
                        control={control}
                        name={`block_data.reviewer_types.${index}.is_anonymous`}
                        render={({ field: { value } }) => (
                          <Switch
                            checked={value}
                            onChange={(nextValue) =>
                              handleReviewerTypeAnonymousToggle(index, nextValue)
                            }
                            title={t("common:anonymous")}
                          />
                        )}
                      />
                    )}
                  </Stack>

                  {invitationMode === ApiPerformanceReviewInvitationMode.by_reviewer_type && (
                    <Stack gap="sm">
                      <Title
                        header={t("performance_review:reviewer_type_invitation")}
                        caption={t("performance_review:reviewer_type_invitation_description")}
                        size="xs"
                        variant="secondary"
                        paddingTop
                      />
                      <ControlledPerformanceReviewInvitationFields
                        control={control}
                        subjectName={`block_data.reviewer_types.${index}.invitation_subject`}
                        bodyName={`block_data.reviewer_types.${index}.invitation_body`}
                        subjectError={
                          errors.block_data?.reviewer_types?.[index]?.invitation_subject?.message
                        }
                        bodyError={
                          errors.block_data?.reviewer_types?.[index]?.invitation_body?.message
                        }
                        variableOptions={getWorkflowPlaceholderOptions(eventType)}
                      />
                    </Stack>
                  )}

                  {canConfigureParticipants && (
                    <Stack>
                      <Title
                        header={t("performance_review:configure_participants")}
                        size={"xs"}
                        variant="secondary"
                        paddingBottom
                        paddingTop
                      />
                      <RuleGroupsEditor
                        control={control}
                        fieldName={
                          `block_data.reviewer_types.${index}.participants_rule_groups` as const
                        }
                        setValue={setValue}
                        place={RuleRegistryPlace.workflowPerformanceReviewReviewers}
                        countEmployees={canPreviewWorkflowRuleGroups(
                          reviewerTypes[index]?.participants_rule_groups,
                          participantConditionTypes.conditionTypeInfos
                        )}
                        conditionTypes={participantConditionTypes.conditionTypes}
                        variant="list"
                        isAddRuleButtonFullWidth={false}
                      />
                    </Stack>
                  )}

                  {showAnonymitySettings && (
                    <Stack gap={"sm"}>
                      <Title
                        header={t("performance_review:settings_anonymity")}
                        size={"xs"}
                        variant="secondary"
                        paddingTop
                      />
                      <Controller
                        control={control}
                        name={`block_data.reviewer_types.${index}.anonymous_threshold`}
                        render={({ field }) => (
                          <Input
                            label={t("performance_review:settings_anonymous_threshold")}
                            type="number"
                            min={ANONYMOUS_THRESHOLD_MIN}
                            value={String(field.value)}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              if (value >= ANONYMOUS_THRESHOLD_MIN) field.onChange(value);
                            }}
                            error={
                              errors.block_data?.reviewer_types?.[index]?.anonymous_threshold
                                ?.message
                            }
                          />
                        )}
                      />
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          );
        })}

        <Box
          className={
            "[&_button]:!text-dark [&_button]:!font-semibold [&_svg[data-icon='chevron-down']]:hidden"
          }
        >
          <Select
            value={null}
            onChange={(value) => {
              if (!value) return;
              if (value === ApiPerformanceReviewReviewerTypes.custom) {
                openModal(PerformanceReviewReviewerTypeAddOrEditModal, {
                  onSave: async (item) => {
                    if (item.title) handleAddCustomReviewerType(item.title);
                  },
                });
              } else {
                handleAddReviewerType(value as WorkflowSystemReviewerType);
              }
            }}
            options={addableReviewerTypeOptions}
            emptyTitle={t("performance_review:add_review_type_workflow")}
            variant="button"
          />
        </Box>

        <input
          {...register(REVIEWER_TYPES_ENABLED_VALIDATION_FIELD)}
          aria-label={t("performance_review:review_types")}
          className={"absolute h-px w-px opacity-0"}
          readOnly
          tabIndex={-1}
        />
        {reviewerTypesErrorMessage && (
          <Box>
            <span className={"text-danger text-sm"}>{reviewerTypesErrorMessage}</span>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
